/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dropdown from '../common/Dropdown/Dropdown';
import Icon from '../common/Icon/Icon';
import { IconKeys } from '../common/Icon/IconKeys';
import S from './UserNotificationsStyles';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetNotifications from '../../API/hooks/notifications/useGetNotifications';
import useDeleteNotification from '../../API/hooks/notifications/useDeleteNotification';
import { logger } from '../../API/services/SharedService';
import { alertEventsMap } from '../../pages/Alerts/constants/alertEventsMap';
import { getLeftPeriodLabel } from '../../utils/dateUtils';
import { Notification } from '../../API/types/Notifications';
import useGetRole from '../../API/hooks/useGetRole';
import { FeaturesManagement } from '../../featuresControl';
import { notificationDetails, getCustomVoDNotificationText } from '../../constants/NotificationDetails';

type NotificationContent = {
  title: string;
  text: string;
  link: string;
  id: string;
};

function UserNotifications({ onSettings }: any): JSX.Element {
  const fleetId = useGetFleetId();
  const navigate = useNavigate();
  const { canAccessNotifications } = useGetRole();
  const [dropdownState, toggleDropdownState] = useState(false);
  const { data: notifications, isLoading: isNotificationsLoading } = useGetNotifications(fleetId);
  const closeDropdown = (): void => toggleDropdownState(false);
  const readSuccess = (): void => {
    if (notifications) {
      notifications.results.length = 0;
      closeDropdown();
    }
    logger.info('Read notification success');
  };
  const readFailure = (e: any): void => logger.error('Read notification failure', e);
  const { mutate: mutateRead, isLoading: isReadLoading } = useDeleteNotification(
    readSuccess,
    readFailure,
  );

  const readAll = (): void => {
    logger.info('UserNotifications', 'readAll');
    mutateRead({ fleetId });
  };

  const notificationOnClick = (e: NotificationContent): any => {
    logger.info('UserNotifications', 'notificationOnClick', e.id);
    mutateRead({
      fleetId,
      notificationId: e.id,
    });
    closeDropdown();
    navigate(e.link);
  };

  const getNotificationContent = (notificationObj: Notification): NotificationContent => {
    let title = {
      title: '',
      text: '',
      link: '',
      id: '',
    };

    if (notificationObj.eventName in alertEventsMap) {
      title = {
        title: alertEventsMap[notificationObj.eventName].shortDesc,
        text: alertEventsMap[notificationObj.eventName].fullDesc,
        link: `/alerts/${notificationObj.entityId}`,
        id: notificationObj._id,
      };
    } else {
      switch (notificationObj.eventName) {
        case 'DevStreamEventCameraBlockageInteriorTotal':
        case 'DevStreamEventCameraBlockageInteriorPartial':
          title = {
            title: 'Int. camera blockage',
            text: notificationObj.eventName === 'DevStreamEventCameraBlockageInteriorTotal' ? notificationDetails.DevStreamEventCameraBlockageInteriorTotal.fullDesc : notificationDetails.DevStreamEventCameraBlockageInteriorPartial.fullDesc,
            link: `/alerts/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        case 'DevStreamEventCameraBlockageExteriorTotal':
        case 'DevStreamEventCameraBlockageExteriorPartial':
          title = {
            title: 'Ext. camera blockage',
            text: notificationObj.eventName === 'DevStreamEventCameraBlockageExteriorTotal' ? notificationDetails.DevStreamEventCameraBlockageExteriorTotal.fullDesc : notificationDetails.DevStreamEventCameraBlockageExteriorPartial.fullDesc,
            link: `/alerts/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        case 'LearnFaceSuccess':
        case 'LearnFaceFail':
        case 'LearnFaceTimedOut':
          title = {
            title: notificationDetails[notificationObj.eventName].shortDesc,
            text: notificationObj.message
              ?? notificationDetails[notificationObj.eventName].fullDesc,
            link: `/drivers/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        case 'RequestAvatarSuccess':
        case 'RequestAvatarFail':
          title = {
            title: 'Learn Face Request',
            text: notificationObj.eventName === 'RequestAvatarSuccess' ? notificationDetails.RequestAvatarSuccess.fullDesc : notificationDetails.RequestAvatarFail.fullDesc,
            link: `/drivers/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        case 'VideoUploadedSuccess':
        case 'VideoUploadedFail':
        case 'VideoQueuedOffline':
        case 'VideoUploadedPartialSuccess':
        case 'VideoNotAvailableOnDevice':
          title = {
            title: 'Custom VoD',
            text: getCustomVoDNotificationText(
              notificationObj.eventName,
              notificationObj.message,
            ),
            link: `/videos/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        case 'AlertVideoUploadedSuccess':
        case 'AlertVideoUploadedFail':
          title = {
            title: 'Alert video',
            text: notificationObj.eventName === 'AlertVideoUploadedSuccess'
              ? notificationDetails.AlertVideoUploadedSuccess.fullDesc
              : notificationDetails.AlertVideoUploadedFail.fullDesc,
            link: `/videos/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        case 'UpdateConfigSuccess':
        case 'UpdateConfigFailed':
          title = {
            title: 'Device settings update',
            text: notificationObj.eventName === 'UpdateConfigSuccess' ? notificationDetails.UpdateConfigSuccess.fullDesc : notificationDetails.UpdateConfigFailed.fullDesc,
            link: `/devices/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        case 'IntersectionsDirectionEntered':
        case 'IntersectionsDirectionLeft':
          title = {
            title: 'Geofence intersection',
            text: notificationObj.eventName === 'IntersectionsDirectionEntered' ? notificationDetails.IntersectionsDirectionEntered.fullDesc : notificationDetails.IntersectionsDirectionLeft.fullDesc,
            link: `/vehicles/${notificationObj.entityId}?tab=geofence`,
            id: notificationObj._id,
          };
          break;
        case 'TripUpsertStart':
        case 'TripUpsertStop':
          title = {
            title: 'Trip status update',
            text: notificationObj.eventName === 'TripUpsertStart' ? notificationDetails.TripUpsertStart.fullDesc : notificationDetails.TripUpsertStop.fullDesc,
            link: `/vehicles/${notificationObj.entityId}`,
            id: notificationObj._id,
          };
          break;
        default:
          title = {
            title: 'Notification',
            text: `Something happened!\nEvent: ${notificationObj.eventName}\nID: ${notificationObj.entityId}\n${notificationObj.message}`,
            link: '/',
            id: notificationObj._id,
          };
      }
    }
    return title;
  };

  return (
    <Dropdown
      target={(
        <S.UserWrapper>
          <div className="icon-container">
            <Icon iconKey={IconKeys.BELL} />
            {!!notifications?.results.length && <span className="badge" />}
          </div>
        </S.UserWrapper>
      )}
      isOpen={dropdownState}
      onChange={toggleDropdownState}
      contentClassName="user-dropdown"
    >
      <S.DropdownWrapper>
        <div className="notifications-header">
          Notifications
          {FeaturesManagement.setting.notificationSettings && canAccessNotifications && (
            <button
              className="notifications-settings"
              type="button"
              onClick={() => {
                toggleDropdownState(false);
                onSettings(true);
              }}
            >
              <Icon iconKey={IconKeys.SETTINGS} />
            </button>
          )}
        </div>
        {!isNotificationsLoading && (notifications?.results.length) ? (
          <>
            <div className="notifications-mark-all">
              <button type="button" onClick={readAll}>Mark all as read</button>
            </div>
            <div className="notifications-content">
              {notifications?.results.map((n, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <button type="button" key={`n${i}`} className="notification-item" onClick={() => notificationOnClick(getNotificationContent(n))}>
                  <div className="notification-icon">
                    <Icon iconKey={IconKeys.DOCUMENT_AWARD} />
                  </div>
                  <div className="notification-body">
                    <div className="notification-title">
                      {getNotificationContent(n).title}
                    </div>
                    <div className="notification-text">
                      <p className="notification-label">
                        {getNotificationContent(n).text}
                      </p>
                    </div>
                    <div className="notification-date">{getLeftPeriodLabel(n.createdAt)}</div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="no-notifications">{isNotificationsLoading ? 'Loading...' : 'No new notifications'}</p>
        )}
      </S.DropdownWrapper>
    </Dropdown>
  );
}

export default UserNotifications;
